import React, { useEffect, useMemo, useState } from 'react';
import {
  Stack, Typography, TextField, Box, Paper, IconButton, Tooltip,
  Button,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';

import SectionCard from './SectionCard';
import HelpTip from '../../common/HelpTip';
import { TableCard } from '../tabLayout';
import { Fact, FactGrid, ListRow, QuietNote, numSx } from './sectionParts';
import { assignKeys } from '../../../utils/fieldKey';
import { AssessmentPathway, TestPaperConfig } from '../../../types/admissions';

const PASS_HELP = {
  title: 'Passing marks',
  text: "Out of the test's total. An applicant below it can't be offered a seat unless an override is approved.",
};

interface Props {
  pathway: AssessmentPathway;
  canEdit: boolean;
  /** Why the section can't change on this open cycle (marks / offers in). */
  lockedReason?: string | null;
  onSave: (patch: Partial<AssessmentPathway>) => Promise<void>;
}

/**
 * TestConfigSection — overall test max/passing + the list of papers
 * (subjects) scored during the entrance test. Papers drive what shows up
 * on the Candidate drawer's Assessment tab.
 */


const TestConfigSection: React.FC<Props> = ({ pathway, canEdit, lockedReason, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    maxMarks: 100,
    passingMarks: 40,
    papers: [] as TestPaperConfig[],
  });

  useEffect(() => {
    if (editing) {
      setDraft({
        maxMarks: pathway.testConfig?.maxMarks ?? 100,
        passingMarks: pathway.testConfig?.passingMarks ?? 40,
        papers: pathway.testConfig?.papers || [],
      });
    }
  }, [editing, pathway.testConfig]);

  const stageEnabled = pathway.stages?.testEnabled;

  const effective = editing ? draft : {
    maxMarks: pathway.testConfig?.maxMarks ?? 100,
    passingMarks: pathway.testConfig?.passingMarks ?? 40,
    papers: pathway.testConfig?.papers || [],
  };

  const totalPaperMarks = useMemo(
    () => effective.papers.reduce((s, p) => s + (p.maxMarks || 0), 0),
    [effective.papers],
  );

  // The test is out of its papers' total — one number, nothing to keep in
  // sync. Passing marks are out of that total.
  const passingTooHigh = effective.papers.length > 0 && effective.passingMarks > totalPaperMarks;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        testConfig: (() => {
          const papers = assignKeys(draft.papers.filter(p => p.subject.trim() && p.maxMarks > 0), p => p.subject, 'paper', 40);
          return {
            maxMarks: papers.reduce((t, p) => t + p.maxMarks, 0) || draft.maxMarks,
            passingMarks: draft.passingMarks,
            papers,
          };
        })(),
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const addPaper = () => setDraft(d => ({
    ...d,
    papers: [...d.papers, { key: '', subject: '', maxMarks: 50 }],
  }));

  const updatePaper = (idx: number, patch: Partial<TestPaperConfig>) =>
    setDraft(d => ({
      ...d,
      papers: d.papers.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    }));

  const removePaper = (idx: number) =>
    setDraft(d => ({ ...d, papers: d.papers.filter((_, i) => i !== idx) }));

  return (
    <SectionCard
      title="Test papers"
      description="Subjects marked in the entrance test. Paper marks count toward the merit score."
      canEdit={canEdit}
      lockedReason={lockedReason}
      editing={editing}
      onEditToggle={setEditing}
      onSave={handleSave}
      onCancel={() => setDraft({
        maxMarks: pathway.testConfig?.maxMarks ?? 100,
        passingMarks: pathway.testConfig?.passingMarks ?? 40,
        papers: pathway.testConfig?.papers || [],
      })}
      saving={saving}
      canSave={!passingTooHigh}
      headerActions={
        editing && (
          <Button size="small" startIcon={<Add fontSize="small" />} onClick={addPaper}>
            Add paper
          </Button>
        )
      }
    >
      <Stack spacing={3}>
        {!stageEnabled && !editing && (
          <QuietNote>The entrance test is off. Turn it on under Stages.</QuietNote>
        )}

        {editing ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 2, alignItems: 'center' }}>
            <Fact label="Total marks">{totalPaperMarks} <Typography component="span" variant="caption" color="text.secondary">(sum of papers)</Typography></Fact>
            <TextField
              size="small"
              label={<HelpTip label="Passing marks" {...PASS_HELP} />}
              type="number"
              inputProps={{ min: 0, max: totalPaperMarks }}
              value={effective.passingMarks}
              onChange={(e) => setDraft(d => ({ ...d, passingMarks: Number(e.target.value) || 0 }))}
              error={passingTooHigh}
              helperText={passingTooHigh ? `At most ${totalPaperMarks}` : `Out of ${totalPaperMarks}`}
            />
          </Box>
        ) : (
          <FactGrid columns={2}>
            <Fact label="Total marks">{totalPaperMarks}</Fact>
            <Fact label={<HelpTip label="Passing marks" {...PASS_HELP} />}>{effective.passingMarks} of {totalPaperMarks}</Fact>
          </FactGrid>
        )}

        {effective.papers.length === 0 ? (
          <QuietNote>No papers yet — add the subjects you'll test, e.g. English, Maths, Urdu.</QuietNote>
        ) : !editing ? (
          <TableCard>
            {effective.papers.map((p, idx) => (
              <ListRow key={p.key || idx}>
                <Typography variant="body2" fontWeight={500} sx={{ flex: 1, minWidth: 0 }}>{p.subject}</Typography>
                <Typography variant="body2" color="text.secondary" sx={numSx}>{p.maxMarks} marks</Typography>
              </ListRow>
            ))}
          </TableCard>
        ) : (
          <Stack spacing={1}>
            {effective.papers.map((p, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <TextField
                    size="small"
                    label="Subject"
                    value={p.subject}
                    onChange={(e) => updatePaper(idx, { subject: e.target.value })}
                    sx={{ flex: 1 }}
                    placeholder="e.g. Mathematics"
                  />
                  <TextField
                    size="small"
                    label="Max marks"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={p.maxMarks}
                    onChange={(e) => updatePaper(idx, { maxMarks: Number(e.target.value) || 0 })}
                    sx={{ width: 140 }}
                  />
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => removePaper(idx)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Stack>
    </SectionCard>
  );
};

export default TestConfigSection;
